import { InfiniteData } from '@tanstack/react-query';

// Copy-on-write helpers for TanStack's infinite-query shape. Each copies only the
// path to the item it touches, and returns the object it was given when nothing
// changed, so an unrelated write skips the `replaceEqualDeep` walk downstream.

/**
 * Replaces the first item matching `match`, copying only root, its page and its
 * slot. Returns `[prev, false]` when not found, `[prev, true]` when `update`
 * returned the item unchanged.
 *
 * @param prev - the cached paginated data
 * @param itemsKey - name of the array field on a page (e.g. `'conversations'`)
 * @param match - predicate selecting the item to update; only the first hit is used
 * @param update - receives the matched item, returns the replacement
 * @returns `[next, found]`
 */
export const updateItemInPages = <TPage, TItem, TPageParam = unknown>(
  prev: InfiniteData<TPage, TPageParam> | undefined,
  itemsKey: keyof TPage & string,
  match: (item: TItem) => boolean,
  update: (item: TItem) => TItem
): [InfiniteData<TPage, TPageParam> | undefined, boolean] => {
  if (!prev || !Array.isArray(prev.pages)) {
    return [prev, false];
  }

  for (let pageIdx = 0; pageIdx < prev.pages.length; pageIdx++) {
    const page = prev.pages[pageIdx];
    const items = page?.[itemsKey] as TItem[] | undefined;
    if (!Array.isArray(items)) {
      continue;
    }

    for (let itemIdx = 0; itemIdx < items.length; itemIdx++) {
      const item = items[itemIdx];
      if (!match(item)) {
        continue;
      }

      const updated = update(item);
      if (updated === item) {
        return [prev, true];
      }

      const nextItems = [...items];
      nextItems[itemIdx] = updated;
      const nextPages = [...prev.pages];
      nextPages[pageIdx] = { ...page, [itemsKey]: nextItems };

      return [{ pages: nextPages, pageParams: prev.pageParams }, true];
    }
  }

  return [prev, false];
};

/**
 * Removes every item matching `match`. Only the pages that lost one are copied;
 * returns `prev` itself when nothing was removed.
 *
 * @param prev - the cached paginated data
 * @param itemsKey - name of the array field on a page
 * @param match - predicate selecting the items to drop
 */
export const removeItemFromPages = <TPage, TItem, TPageParam = unknown>(
  prev: InfiniteData<TPage, TPageParam> | undefined,
  itemsKey: keyof TPage & string,
  match: (item: TItem) => boolean
): InfiniteData<TPage, TPageParam> | undefined => {
  if (!prev || !Array.isArray(prev.pages)) {
    return prev;
  }

  let nextPages: TPage[] | undefined;

  for (let pageIdx = 0; pageIdx < prev.pages.length; pageIdx++) {
    const page = prev.pages[pageIdx];
    const items = page?.[itemsKey] as TItem[] | undefined;
    if (!Array.isArray(items)) {
      continue;
    }

    // Scan before allocating: a page that keeps everything costs no array.
    const firstHit = items.findIndex(match);
    if (firstHit === -1) {
      continue;
    }

    const kept = items.slice(0, firstHit);
    for (let itemIdx = firstHit + 1; itemIdx < items.length; itemIdx++) {
      if (!match(items[itemIdx])) {
        kept.push(items[itemIdx]);
      }
    }

    nextPages = nextPages ?? [...prev.pages];
    nextPages[pageIdx] = { ...page, [itemsKey]: kept };
  }

  return nextPages ? { pages: nextPages, pageParams: prev.pageParams } : prev;
};

/** Prepends to the first page. Returns `prev` when there are no pages. */
export const prependItemToFirstPage = <TPage, TItem, TPageParam = unknown>(
  prev: InfiniteData<TPage, TPageParam> | undefined,
  itemsKey: keyof TPage & string,
  item: TItem
): InfiniteData<TPage, TPageParam> | undefined => {
  if (!prev || !Array.isArray(prev.pages) || prev.pages.length === 0) {
    return prev;
  }

  const page = prev.pages[0];
  const items = page?.[itemsKey] as TItem[] | undefined;
  const nextPages = [...prev.pages];
  nextPages[0] = { ...page, [itemsKey]: [item, ...(Array.isArray(items) ? items : [])] };

  return { pages: nextPages, pageParams: prev.pageParams };
};

// Appends to the last page. Returns `prev` when there are no pages.
export const appendItemToLastPage = <TPage, TItem, TPageParam = unknown>(
  prev: InfiniteData<TPage, TPageParam> | undefined,
  itemsKey: keyof TPage & string,
  item: TItem
): InfiniteData<TPage, TPageParam> | undefined => {
  if (!prev || !Array.isArray(prev.pages) || prev.pages.length === 0) {
    return prev;
  }

  const lastIdx = prev.pages.length - 1;
  const page = prev.pages[lastIdx];
  const items = page?.[itemsKey] as TItem[] | undefined;
  const nextPages = [...prev.pages];
  nextPages[lastIdx] = { ...page, [itemsKey]: [...(Array.isArray(items) ? items : []), item] };

  return { pages: nextPages, pageParams: prev.pageParams };
};
